<script lang="ts" setup>
	type ItemRecord = {
		name?: string
		key: unknown
		value?: unknown
		history?: { label: string; value: unknown }[]
		active?: boolean
	}
	export type ListItemDropdownContext = Record<string, ItemRecord>

	const props = defineProps<{
		listItems?: ListItemDropdownContext
		loading?: boolean
	}>()
	const list = ref<ListItemDropdownContext>()
	const { currencyToNaira } = useCurrency()

	const toggle = (arg: ItemRecord) => {
		arg.active = !arg.active
	}

	watch(
		() => props.listItems,
		result => {
			list.value = result
		},
		{
			immediate: true
		}
	)
</script>

<template>
	<div>
		<loader-skeleton v-if="loading" :height="200" :count="1" :max-width="false" />
		<ul v-else>
			<li v-for="(item, k) in list" :key="k" class="border-gray-200 text-sm">
				<div
					class="flex items-center justify-between border-b py-5 text-sm"
					role="button"
					aria-describedby="button"
					aria-label="Unwrap list items"
					:aria-expanded="item.active"
					@click="toggle(item)"
				>
					<span class="capitalize text-gray-500">
						<slot :name="`title(${item.key})`" :title="k" :content="item">
							{{ item?.name || k }}
						</slot>
					</span>

					<div class="flex items-center gap-x-2">
						<span class="font-medium text-gray-600">
							<slot :name="`value(${item.key})`" :value="item?.value" :content="item">
								{{ typeof item?.value === 'number' ? currencyToNaira(item?.value) : item?.value }}
							</slot>
						</span>
						<lazy-tools-icon
							v-if="item.history?.length"
							icon="caret-down-r"
							dir="arrows"
							class="transition"
							:class="[item.active ? 'rotate-180' : 'rotate-0']"
							path-class="stroke-primary-2 "
							:height="20"
							:width="20"
						/>
					</div>
				</div>

				<div
					class="custom-transition relative overflow-y-hidden"
					:class="[item.active ? 'max-h-[2000px]' : 'max-h-0']"
				>
					<div
						v-for="(history, hIndex) in item.history"
						:key="hIndex"
						class="flex items-center justify-between border-b py-4 text-xs"
					>
						<span class="text-gray-500">
							{{ history?.label }}
						</span>

						<span
							:class="[
								typeof history.value === 'number' && history.value < 20
									? 'text-error-500'
									: 'text-gray-600'
							]"
						>
							{{ typeof history.value === 'number' ? currencyToNaira(history.value) : history.value }}
						</span>
					</div>
				</div>
			</li>
		</ul>
	</div>
</template>
